import { AnalyticsType } from '@/types/generated/common/types'
import type { DataPayload } from '@/types/generated/public_api'

// Instrument-keyed live analytics: Hyperliquid liquidation & resting-stop price profiles
// (oems #261) and the perp top-positions table (oems #560). Kept separate from
// service/amberdata/* — these aren't Amberdata-sourced, and reusing that module's currency-keyed
// symbol handling (normalizeAmberdataSymbol etc.) would be wrong here: these types are keyed
// on a full instrument symbol (e.g. "BTC-PERP"), not a bare currency.
export const LEVEL_PROFILE_PAYLOAD_KEYS = {
  [AnalyticsType.ANALYTICS_TYPE_LIQUIDATION_LEVELS]: 'liquidationLevelProfile',
  [AnalyticsType.ANALYTICS_TYPE_STOP_LEVELS]: 'stopLevelProfile',
  [AnalyticsType.ANALYTICS_TYPE_PERP_POSITIONS]: 'perpPositions',
} as const satisfies Partial<Record<AnalyticsType, keyof DataPayload>>

export type LevelProfileAnalyticsType = keyof typeof LEVEL_PROFILE_PAYLOAD_KEYS

export type LevelProfileFor<T extends LevelProfileAnalyticsType> = NonNullable<
  DataPayload[(typeof LEVEL_PROFILE_PAYLOAD_KEYS)[T]]
>

export function isLevelProfileAnalyticsType(
  analyticsType: AnalyticsType
): analyticsType is LevelProfileAnalyticsType {
  return analyticsType in LEVEL_PROFILE_PAYLOAD_KEYS
}

export function getLevelProfilePayloadKey<T extends LevelProfileAnalyticsType>(
  analyticsType: T
): (typeof LEVEL_PROFILE_PAYLOAD_KEYS)[T] {
  return LEVEL_PROFILE_PAYLOAD_KEYS[analyticsType]
}

export function extractLevelProfile<T extends LevelProfileAnalyticsType>(
  payload: DataPayload | undefined,
  analyticsType: T
): LevelProfileFor<T> | undefined {
  if (!payload) return undefined
  const key = getLevelProfilePayloadKey(analyticsType)
  return payload[key] as LevelProfileFor<T> | undefined
}

// `undefined` is deliberately not part of this union — a plain `T | undefined` return can't
// distinguish "still loading" from "confirmed not published" (NotFound), which the UI needs to
// render two different messages for.
export type LevelProfileSnapshot<T> =
  | { status: 'published'; profile: T }
  | { status: 'not-published' }
